import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import * as zVal from '../../../../lib/validations';
import { Separator } from '../../../ui/separator';
import PersonalInfo from './personal-info';
import AccessibilityInfo from './accessibility-info';
import AddressInfo from './address-info';
import MailAddressInfo from './mail-address-info';
import EmergencyContact from './emergency-contact';
import PersonalContact from './personal-contact';
import ElectionReasons from './election-reasons';
import { reasons } from '../constants';
import Button from '../../../ui/button';
import Text from '../../../ui/text';
import i18n from '../../../../lib/i18n';
import { accessibleFormats as accessibleFormatsList } from '../constants';
import { useMemo } from 'react';

const schema = z.object({
  firstName: zVal.textRequired(),
  middleName: zVal.textOptionalEmpty(),
  lastName: zVal.textRequired(),
  dateOfBirth: zVal.dateRequired(),
  // describesYou: zVal.textOptionalEmpty(),
  // describesYouOther: zVal.textOptionalEmpty(),
  sex: zVal.textOptionalEmpty(),
  // gender: zVal.textOptionalEmpty(),
  // genderOther: zVal.textOptionalEmpty(),
  race: zVal.textOptionalEmpty(),
  ethnicity: zVal.textOptionalEmpty(),
  yourSpouseWork: z.boolean(),
  experiencingHomelessness: z.boolean().optional().default(false),
  addressLine1: zVal.textOptionalEmpty(),
  addressLine2: zVal.textOptionalEmpty(),
  city: zVal.textRequired(),
  state: zVal.textRequired(),
  zipCode: zVal.zipCodeRequired(),
  differentMailingAddress: z.boolean().optional().default(false),
  mailAddressLine1: zVal.textOptionalEmpty(),
  mailAddressLine2: zVal.textOptionalEmpty(),
  mailCity: zVal.textOptionalEmpty(),
  mailState: zVal.textOptionalEmpty(),
  mailZipCode: zVal.zipCodeOptionalEmpty(),
  emergencyContactName: zVal.textRequired(),
  emergencyPhoneNumber: zVal.phoneRequired(),
  emergencyRelationship: zVal.textRequired(),
  mainPhoneNumber: zVal.phoneRequired(),
  alternatePhoneNumber: zVal.phoneOptionalEmpty(),
  language: zVal.textOptionalEmpty(),
  accessibleFormatsOptions: zVal.textOptionalEmpty(),
  email: zVal.emailOptionalEmpty(),
  accessibilityOptions: z.array(zVal.textOptionalEmpty()).optional(),
  electionReasons: z.array(z.object({ 
    checked: z.boolean().optional(), 
    date: z.string()
    .optional()
    .refine(value => value === undefined || value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value), {
      message: i18n.t('Invalid date format'),
    }),
    dateTo: z.string()
    .optional()
    .refine(value => value === undefined || value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value), {
      message: i18n.t('Invalid date format'),
    }),
    text: z.string().min(1, i18n.t('Required')).optional().or(z.literal('')),
  })),
}).superRefine((values, context) => {

  const requiredMessage = i18n.t('Required');

  /* if (values.describesYou && values.describesYou === '4' && !values.describesYouOther) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["describesYouOther"],
    });
  } */

  /* if (values.gender && values.gender === '4' && !values.genderOther) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["genderOther"],
    });
  } */

  if (values.differentMailingAddress && !values.mailAddressLine1) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["mailAddressLine1"],
    });
  }

  if (values.differentMailingAddress && !values.mailState) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["mailState"],
    });
  }

  if (values.differentMailingAddress && !values.mailZipCode) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: ["mailZipCode"],
    });
  }

  if (values.electionReasons?.length > 0) {
    values.electionReasons.forEach((item, index) => {
      if (item.checked && reasons[index].hasDateInput && !item.date) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: [`electionReasons.${index}.date`]
        });
      }
      if (item.checked && reasons[index].hasDateInputTo && !item.dateTo) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: [`electionReasons.${index}.dateTo`]
        });
      }
      if (item.checked && reasons[index].hasTextInput && (!item.text || item.text === '')) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: [`electionReasons.${index}.text`]
        });
      }
    });
  }
  
  return z.NEVER; // The return value is not used, but we need to return something to satisfy the typing
});

const getAccesibleFormatsValues = (selectedFormatsString) => {
  // If no formats selected, return all N
  if (!selectedFormatsString || selectedFormatsString.trim() === '') {
    return {
      accessibleFormats: null,
      AccessibleFormat: null,
      AccessBraile: 'N',
      AccessLargePrint: 'N',
      AccessAudioCD: 'N',
      AccessDataCD: 'N',
    };
  }

  // Split the comma-separated string into an array
  const selectedFormats = selectedFormatsString.split(',').map(f => f.trim());
  
  // Find the first format that appears in accessibleFormatsList (priority order)
  let accessibleFormat = '';
  for (const format of accessibleFormatsList) {
    if (selectedFormats.includes(format.value)) {
      accessibleFormat = format.value;
      break;
    }
  }

  // Check if each specific format is in the selected list
  return {
    accessibleFormats: accessibleFormat,
    AccessibleFormat: accessibleFormat,
    AccessBraile: selectedFormats.includes('Braille') ? 'Y' : 'N',
    AccessLargePrint: selectedFormats.includes('LargePrint') ? 'Y' : 'N',
    AccessAudioCD: selectedFormats.includes('AudioCD') ? 'Y' : 'N',
    AccessDataCD: selectedFormats.includes('DataCD') ? 'Y' : 'N',
  };
}

function PersonalDetails(props) {
  const { goToTab, updateData, data } = props;
  //const planYear = useMemo(() => data?.planYear, [data]);
  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {}
  });
  const {
    register,
    handleSubmit,
    setFocus,
    watch,
    formState: { errors },
  } = methods;

  const onSubmit = (newData) => {

    const reasonsValues = [];
    newData.electionReasons?.forEach((item, index) => {
      if (item.checked) {
        reasonsValues.push({
          id: reasons[index]?.value,
          date: item.date || '',
          dateTo: item.dateTo || '',
          text: item.text || '',
        });
      }
    });

    const accessibleFormatsOptionsText = newData.accessibleFormatsOptions;
    const accessibleFormatsValues = getAccesibleFormatsValues(accessibleFormatsOptionsText);
    delete newData.accessibleFormatsOptions;

    updateData({
      ...newData,
      electionReasons: reasonsValues,
      ...accessibleFormatsValues,
    });

    goToTab('health');
  };

  useEffect(() => {
    const firstError = Object.keys(errors).reduce((field, a) => {
      return !!errors[field] ? field : a;
    }, null);
  
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  return (
    <main className="flex">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="flex flex-col items-start self-center w-full">
            <div className='w-full'>
              <PersonalInfo />
            </div>
            <div className='w-full py-10'>
              <Separator />
            </div>
            <div className='w-full'>
              <AddressInfo />
            </div>
            <div className='w-full py-10'>
              <Separator />
            </div>
            <div className='w-full'>
              <MailAddressInfo />
            </div>
            <div className='w-full py-10'>
              <Separator />
            </div>
            <div className='w-full'>
              <PersonalContact />
            </div>
            <div className='w-full py-10'>
              <Separator />
            </div>
            <div className='w-full'>
              <EmergencyContact />
            </div>
            <div className='w-full py-10'>
              <Separator />
            </div>
            <div className='w-full'>
              <AccessibilityInfo />
            </div> 
            <div className='w-full py-10'>
              <Separator />
            </div>
            <div className='w-full'>
              <ElectionReasons />
            </div>
            {/*  */}
          </div>
          <div className='w-full'>
            <div className="w-full flex flex-row justify-end gap-3 mt-5">
              <Button type='submit' color='primary' radius='full'><Text>Next</Text></Button>
            </div>
          </div>     
        </form>
      </FormProvider>
    </main>
  );
}

export default PersonalDetails;